import React, { useLayoutEffect, useRef, useState } from 'react';
import { FormattedDateTimeWithSeconds } from '../../../../Components/FormattedDate';
import { DiscourseItem } from '../DiscourseHistory/DiscourseHistoryItem';
import LinkButton from '../../../../Components/Button/LinkButton';
import { Theme } from '../../../../Enums/Theme.enum';
import useTranslation from '../../../../Hooks/useTranslation';
import { joinClasses } from '../../../../Utils/className.util';
import styles from './DiscourseSelectItem.module.scss';

interface Props {
	item: DiscourseItem;
}

const DiscourseSelectItem: React.FC<Props> = ({ item }) => {
	const { t } = useTranslation();
	const textRef = useRef<HTMLDivElement>(null);
	const [isExpanded, setIsExpanded] = useState(false);
	const [hasOverflow, setHasOverflow] = useState(false);

	const datetimeText = item.items?.find(subItem => subItem.type === 'datetime')
		?.extra?.text as string | undefined;

	const headerDatetime: Date | string = datetimeText
		? new Date(datetimeText)
		: item.createdAt;

	const isDraft = item.type === 'DEKURZ_DRAFT';

	const name = (
		item.items?.find(subItem => subItem.type === 'name')?.extra?.text as
			| string
			| undefined
	)?.trim();

	const mainDiagnosis = item.items?.find(
		subItem =>
			subItem.type === 'diagnosis' &&
			subItem.extra?.subType === 'main_diagnosis',
	);

	const secondaryDiagnoses = item.items?.filter(
		subItem =>
			subItem.type === 'diagnosis' &&
			subItem.extra?.subType === 'secondary_diagnosis',
	);

	const contentText = item.items
		?.filter(subItem => subItem.type === 'content')
		.map(subItem => subItem.extra?.text as string)
		.filter(Boolean)
		.join(' ');

	useLayoutEffect(() => {
		const el = textRef.current;
		if (!el || !contentText) {
			setHasOverflow(false);
			return;
		}
		if (isExpanded) {
			return;
		}
		setHasOverflow(el.scrollHeight > el.clientHeight + 1);
	}, [contentText, isExpanded]);

	const showTextToggle = Boolean(contentText) && (hasOverflow || isExpanded);

	return (
		<div className={styles.wrapper}>
			<div className={styles.header}>
				<div className={styles.left}>
					<span className={styles.date}>
						<FormattedDateTimeWithSeconds datetime={headerDatetime} />
					</span>
					{isDraft && (
						<span className={styles.draftBadge}>
							{t('component.discourseSelect.draftBadge')}
						</span>
					)}
				</div>
				{item.createdBy?.profile?.fullName && (
					<div className={styles.meta}>
						{t('base.wrote')}: {item.createdBy.profile.fullName}
					</div>
				)}
			</div>
			{name && <span className={styles.name}>{name}</span>}
			<div className={styles.diagnosisWrapper}>
				{mainDiagnosis && (
					<div className={styles.diagnosis}>
						<span className={styles.diagnosisLabel}>
							{t('component.discourse.mainDiagnosis')}:
						</span>
						{mainDiagnosis.extra?.text?.label}
					</div>
				)}
				{secondaryDiagnoses?.map((diag, index) => (
					<div key={index} className={styles.diagnosis}>
						<span className={styles.diagnosisLabel}>
							{t('component.discourse.secondaryDiagnosis')}:
						</span>
						{diag.extra?.text?.label}
					</div>
				))}
			</div>
			{contentText && (
				<div className={styles.textBlock}>
					<div
						ref={textRef}
						className={joinClasses([
							styles.text,
							!isExpanded && styles.textCollapsed,
						])}>
						{contentText}
					</div>
					{showTextToggle && (
						<div
							className={styles.textToggle}
							onClick={e => e.stopPropagation()}>
							<LinkButton
								theme={Theme.BLUE}
								title={isExpanded ? 'base.showLess2' : 'base.more'}
								className={styles.textToggleLink}
								onClick={() => setIsExpanded(v => !v)}
							/>
						</div>
					)}
				</div>
			)}
		</div>
	);
};

export default DiscourseSelectItem;
